import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, FileText, Users, Calendar, Award } from 'lucide-react';
import Button from './Button';
import './ScholarshipsPage.css';

const ScholarshipsPage: React.FC = () => {
  return (
    <div className="scholarships-page">
      <Helmet>
        <title>Scholarships – Carly Faye Foundation</title>
        <meta name="description" content="Explore academic and athletic scholarships from the Carly Faye Foundation and learn how to apply." />
        <link rel="canonical" href="https://carly-faye-foundation.web.app/scholarships" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Scholarships – Carly Faye Foundation" />
        <meta property="og:description" content="Academic and athletic scholarships supporting young women in their pursuit of excellence." />
        <meta property="og:url" content="https://carly-faye-foundation.web.app/scholarships" />
        <meta property="og:image" content="/images/carly-faye-athletic.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Scholarships – Carly Faye Foundation" />
        <meta name="twitter:description" content="Explore academic and athletic scholarships and how to apply." />
        <meta name="twitter:image" content="/images/carly-faye-athletic.jpg" />
      </Helmet>
      <section className="scholarships-intro">
        <div className="container">
          <div className="scholarships-intro-content">
            <h2>Scholarship Programs</h2>
            <p>
              The Carly Faye Foundation offers two distinct scholarship programs designed to support 
              young women in their pursuit of academic and athletic excellence. Each program honors 
              different aspects of Carly's legacy and provides financial support to deserving students.
            </p>
          </div>
        </div>
      </section>

      <section className="scholarship-section">
        <div className="container">
          <div className="scholarships-content">
            {/* Athletic Scholarship Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="scholarship-card"
            >
              <div className="scholarship-image">
                    <img src="/images/carly-faye-athletic.jpg" alt="Athletic Scholarship" />
                    <div className="scholarship-image-accent"></div>
                  </div>
                  
                  <div className="scholarship-content">
                    <h2>Athletic Scholarship</h2>
                    <p className="scholarship-description">
                      The <strong>Carly Faye Foundation Athletic Scholarship</strong> is awarded to a graduating senior 
                      volleyball player who demonstrates passion, love for the game, teamwork, and dedication both on 
                      and off the court, embodying the spirit that Carly brought to the sport.
                    </p>
                    
                    <div className="scholarship-criteria">
                      <h3>Eligibility Criteria</h3>
                      <ul>
                        <li>Applicant must be a graduating high school senior</li>
                        <li>Applicant must be a volleyball player</li>
                        <li>Applicant must demonstrate passion and dedication to the sport</li>
                        <li>Applicant must submit a 1-page letter to the committee about volleyball experience</li>
                        <li>Applicant must submit 2 letters of recommendation</li>
                      </ul>
                    </div>
                    
                    <div className="scholarship-deadline">
                      <Calendar size={20} />
                      <p><strong>Application Deadline:</strong> April 10, 2026</p>
                    </div>
                    
                    <div className="scholarship-actions">
                      <Button 
                        href="/scholarships/apply/athletic"
                        variant="primary"
                        icon={ArrowRight}
                      >
                        Apply Now
                      </Button>
                      <Button 
                        variant="secondary"
                        icon={FileText}
                        href="/contact"
                      >
                        Submit Recommendation
                      </Button>
                    </div>
              </div>
            </motion.div>

            {/* Academic Scholarship Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="scholarship-card"
            >
              <div className="scholarship-image">
                    <img src="/images/carly/IMG_7699.JPG" alt="Academic Scholarship" />
                    <div className="scholarship-image-accent"></div>
                  </div>
                  
                  <div className="scholarship-content">
                    <h2>Academic Scholarship</h2>
                    <p className="scholarship-description">
                      The <strong>Carly Faye Foundation Academic Scholarship</strong> provides support to a graduating senior 
                      from Catholic Central High School who intends to pursue a course of study at a university or college 
                      and demonstrates hard work and dedication to academia that was characteristic of Carly DeLong.
                    </p>
                    
                    <div className="scholarship-criteria">
                      <h3>Eligibility Criteria</h3>
                      <ul>
                        <li>Applicant must attend Catholic Central High School</li>
                        <li>Applicant must be a graduating high school senior in the year of the award</li>
                        <li>Applicant must have at least a 3.0 grade point average</li>
                        <li>Applicant must submit a 1-page essay</li>
                        <li>Applicant must submit 2 letters of recommendation from non-family members</li>
                      </ul>
                    </div>
                    
                    <div className="scholarship-deadline">
                      <Calendar size={20} />
                      <p><strong>Application Deadline:</strong> April 10, 2026</p>
                    </div>
                    
                    <div className="scholarship-actions">
                      <Button 
                        href="/scholarships/apply/academic"
                        variant="primary"
                        icon={ArrowRight}
                      >
                        Apply Now
                      </Button>
                      <Button 
                        variant="secondary"
                        icon={FileText}
                        href="/contact"
                      >
                        Submit Recommendation
                      </Button>
                    </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="application-process">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="process-content"
          >
            <h2>Application Process</h2>
            <div className="process-steps">
              <div className="process-step">
                <div className="process-icon">
                  <FileText size={24} />
                </div>
                <h3>1. Complete Application</h3>
                <p>Fill out the online application form with all required information.</p>
              </div>
              
              <div className="process-step">
                <div className="process-icon">
                  <Users size={24} />
                </div>
                <h3>2. Submit Documents</h3>
                <p>Upload your essay/letter, transcript, and have recommendations submitted.</p>
              </div>
              
              <div className="process-step">
                <div className="process-icon">
                  <Calendar size={24} />
                </div>
                <h3>3. Application Review</h3>
                <p>Applications are reviewed after the April 10, 2026 deadline.</p>
              </div>
              
              <div className="process-step">
                <div className="process-icon">
                  <Award size={24} />
                </div>
                <h3>4. Award Notification</h3>
                <p>Selected recipients will be notified and honored at an award ceremony.</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="cta-content"
          >
            <h2>Support Our Scholarship Programs</h2>
            <p>
              Help us continue Carly's legacy by supporting deserving students 
              in their academic and athletic pursuits.
            </p>
            <Link to="/donate" className="primary-button">
              Donate Now <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default ScholarshipsPage; 